import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/common";
import { AdminLayout } from "@/pages/admin/adminShared";
import { formatDateShort } from "@/lib/format";
import { Trash2, Mail, MapPin, CalendarDays, Inbox } from "lucide-react";

export default function ManageSubmissions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: subs, isLoading } = useQuery({ queryKey: ["event-submissions"], queryFn: async () => (await api.get("/admin/event-submissions")).data });
  const del = async (s) => { await api.delete(`/admin/event-submissions/${s.id}`); qc.invalidateQueries({ queryKey: ["event-submissions"] }); toast("Submission removed"); };

  return (
    <AdminLayout role={user?.role} title="Event submissions" subtitle="Events sent in by neighbouring clubs for you to review and add.">
      {isLoading ? <Spinner /> : !subs?.length ? (
        <EmptyState title="No submissions yet" subtitle="When another club submits an event, it'll appear here for you to review." icon={Inbox} />
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`submission-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold text-[#2D1E18]">{s.event_title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6E5B51]">
                    {s.event_date && <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {formatDateShort(s.event_date)}</span>}
                    {s.venue && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {s.venue}</span>}
                  </div>
                </div>
                <button onClick={() => del(s)} className="rounded-lg p-1.5 text-[#6E5B51] hover:text-[#991B1B]" aria-label="Delete" data-testid={`submission-delete-${s.id}`}><Trash2 size={16} /></button>
              </div>
              {s.details && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#5C4A42]">{s.details}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#5C4A42]">
                <span className="font-semibold">{s.contact_name}{s.club_name ? ` · ${s.club_name}` : ""}</span>
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 font-semibold text-[#B04B26] hover:underline"><Mail size={14} /> {s.email}</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
